/* Admin desk — workspace owner's control surface.
   Active users + spend, per-dev API keys & limits, team build hierarchy,
   and dev feedback / reported issues. Reads window.MAV.{team,pods,issues}. */

const { useState: useAdminState } = React;

/* ---------- CSV export: every number an admin might want to crunch ---------- */
function exportAdminCSV(MAV) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const toCSV = (rows) => rows.map(r => r.map(esc).join(',')).join('\r\n');
  const stamp = new Date().toISOString().slice(0, 10);
  const sections = [];

  // 1 — per-user tracking: tokens, spend, budget, ratings, afterburners, org path
  const userHead = ['member_id', 'name', 'role', 'department', 'sub_department', 'team',
    'status', 'builds_mtd', 'tokens_k_mtd', 'spend_usd', 'monthly_limit_usd', 'used_usd',
    'budget_remaining_usd', 'utilization_pct', 'last_build_rating', 'afterburners_left',
    'afterburners_cap', 'api_key_suffix', 'last_build'];
  const userRows = MAV.team.map(m => {
    const path = MAV.teamPath(m.pod) || {};
    const remaining = m.limit === 0 ? '' : (m.limit - m.used);
    const util = m.limit === 0 ? '' : Math.round(m.used / m.limit * 100);
    return [m.id, m.name, m.role, path.dept || '', path.subDept || '', path.team || '',
      m.status, m.builds, m.tokensK, m.spend, m.limit === 0 ? 'unlimited' : m.limit, m.used,
      remaining, util, m.rating, m.afterburnersLeft ?? '', m.afterburnersCap ?? '',
      'sk-or-••••' + m.key, m.lastBuild || ''];
  });
  sections.push('# Members — usage & budget (' + stamp + ')');
  sections.push(toCSV([userHead, ...userRows]));

  // 2 — per-team rollup
  const teamHead = ['team_id', 'team', 'department', 'sub_department', 'lead', 'devs',
    'builds_mtd', 'tokens_k_mtd', 'spend_usd', 'assigned_clients'];
  const teamRows = MAV.pods.map(p => {
    const mem = MAV.team.filter(m => m.pod === p.id);
    const agg = mem.reduce((a, m) => ({ b: a.b + m.builds, t: a.t + m.tokensK, s: a.s + m.used }), { b: 0, t: 0, s: 0 });
    const path = MAV.teamPath(p.id) || {};
    return [p.id, p.name, path.dept || '', path.subDept || '', p.lead, mem.length, agg.b, agg.t, agg.s, p.clients];
  });
  sections.push('');
  sections.push('# Teams — rollup');
  sections.push(toCSV([teamHead, ...teamRows]));

  // 3 — per-build metadata: stages, tokens, cost, time, verdict, confidence
  const buildHead = ['build_id', 'name', 'client', 'source', 'status', 'verdict',
    'owner', 'started', 'tokens_k', 'cost_usd', 'client_confidence_pct', 'stages_done',
    'stages_total', 'total_stage_seconds', 'per_stage_tokens', 'per_stage_seconds'];
  const buildRows = MAV.jobs.map(j => {
    const done = j.stages.filter(s => s.status === 'done').length;
    const secs = j.stages.reduce((sum, s) => sum + (parseInt(s.elapsed) || 0), 0);
    const stoks = j.stages.map(s => s.name + ':' + (s.tokens || '—')).join(' | ');
    const ssecs = j.stages.map(s => s.name + ':' + (s.elapsed || '—')).join(' | ');
    return [j.id, j.name, (MAV.clientBySlug(j.client) || {}).name || j.client, j.source,
      j.status, j.verdict || '', j.owner, j.started, j.tokensK, j.cost,
      Math.round((j.confidence || 0) * 100), done, j.stages.length, secs, stoks, ssecs];
  });
  sections.push('');
  sections.push('# Builds — per-job metadata');
  sections.push(toCSV([buildHead, ...buildRows]));

  // 4 — issues / quality log
  const issHead = ['issue_id', 'title', 'severity', 'status', 'state', 'build', 'reporter', 'assignee', 'opened', 'labels'];
  const issRows = (MAV.issues || []).map(it => {
    const rep = MAV.memberById(it.reporter || it.dev) || {};
    const asg = MAV.memberById(it.assignee) || {};
    return [it.id, it.title || '', it.sev, it.status, it.state || '', it.build || '',
      rep.name || it.reporter || it.dev || '', asg.name || 'unassigned', it.when || '',
      (it.labels || []).join(' ')];
  });
  sections.push('');
  sections.push('# Issues — quality log');
  sections.push(toCSV([issHead, ...issRows]));

  // 4b — per-client build volume
  const cliHead = ['client', 'slug', 'team', 'builds_this_week', 'builds_this_month', 'builds_all_time'];
  const cliRows = MAV.clients.map(c => {
    const st = MAV.clientBuildStats(c.slug);
    const team = MAV.pods.find(p => (p.clientSlugs || []).includes(c.slug));
    return [c.name, c.slug, team ? team.name : '', st.week, st.month, st.total];
  });
  sections.push('');
  sections.push('# Clients — build volume');
  sections.push(toCSV([cliHead, ...cliRows]));

  // 5 — workspace totals (same live wiring as the Admin tiles + Usage page):
  // spend = OpenRouter account usage when live, else DB total_cost, else per-member
  // (0 until owner_id); tokens = sum of loaded builds' real tokensK.
  const _sumA = MAV.summary || {};
  const _crA = MAV.credits || null;
  const _orLiveA = !!(_crA && _crA.available && _crA.remaining != null);
  const _jobsTokKA = (MAV.jobs || []).reduce((s, j) => s + (Number(j.tokensK) || 0), 0);
  const totSpend = _orLiveA ? Number(_crA.total_usage) : ((_sumA.total_cost != null ? Number(_sumA.total_cost) : 0) || MAV.team.reduce((s, m) => s + (m.used || 0), 0));
  const totTokK = _jobsTokKA > 0 ? _jobsTokKA : MAV.team.reduce((s, m) => s + (m.tokensK || 0), 0);
  const totBuilds = MAV.team.reduce((s, m) => s + m.builds, 0);
  sections.push('');
  sections.push('# Workspace — totals');
  sections.push(toCSV([
    ['metric', 'value'],
    ['report_generated', new Date().toISOString()],
    ['active_users', MAV.team.length],
    ['teams', MAV.pods.length],
    ['builds_mtd', totBuilds],
    ['tokens_k_mtd', totTokK],
    ['spend_usd_mtd', totSpend],
    ['workspace_cap_usd', 3000],
    ['cap_utilization_pct', Math.round(totSpend / 3000 * 100)],
    ['avg_build_rating', (MAV.team.reduce((s, m) => s + m.rating, 0) / MAV.team.length).toFixed(2)],
    ['open_issues', MAV.issues.filter(i => i.status === 'open').length],
  ]));

  const blob = new Blob(['\uFEFF' + sections.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'mavlers-admin-export-' + stamp + '.csv';
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------- small atoms ---------- */

function Avatar({ m, size = 36 }) {
  return (
    <div className="avatar-wrap">
      <div className="avatar" style={{ width: size, height: size, fontSize: size * 0.36, background: m.color }}>{m.initials}</div>
      <span className={'status-dot ' + m.status} title={m.status}></span>
    </div>
  );
}

function Stars({ n, size = 14 }) {
  return (
    <span className="stars" aria-label={n + ' of 5'}>
      {[1, 2, 3, 4, 5].map(i => (
        i <= n
          ? <I.star key={i} size={size} fill="var(--brand-amber)" />
          : <I.star key={i} size={size} className="empty" />
      ))}
    </span>
  );
}

function MemberCell({ m }) {
  return (
    <div className="row gap-12" style={{ minWidth: 0 }}>
      <Avatar m={m} />
      <div style={{ minWidth: 0 }}>
        <div className="nm" style={{ fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
        <div className="muted" style={{ fontSize: 11.5 }}>{m.role}</div>
      </div>
    </div>
  );
}

/* ============================================================
   Admin desk
   ============================================================ */
function AdminDesk({ go, initialIssue }) {
  const MAV = window.MAV;
  // initialIssue: global-search deep-link. Seeds straight to the open ticket; the parent
  // remounts by key when the target changes, so no effect is needed. The route itself stays
  // RBAC-gated upstream — this only picks which tab a permitted viewer lands on.
  const [tab, setTab] = useAdminState(initialIssue ? 'feedback' : 'team');   // team | builds | feedback
  const [fbTab, setFbTab] = useAdminState(initialIssue ? 'issues' : 'ratings');  // ratings | issues
  const [openIssueId, setOpenIssueId] = useAdminState(initialIssue || null);
  // RBAC-05: who can manage members → gates the "Members & access" tab. Derived
  // from the live /me (orgTier + a team_lead/admin seat). The SERVER (member_scope_ok)
  // enforces every mutation — this is presentation only.
  // TODO RBAC-07: source the viewer from the shared /me adapter, not a raw me().
  const [me, setMe] = useAdminState(null);
  React.useEffect(() => {
    let alive = true;
    (window.GooseAPI && window.GooseAPI.me ? window.GooseAPI.me() : Promise.reject())
      .then((m) => { if (alive && m) setMe(m); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const canManageMembers = !!(me && (me.orgTier != null || (me.memberships || []).some((m) => m.role === 'team_lead' || m.role === 'admin')));  // ORG-01 seats
  // Live OpenRouter account usage for the Total-spend tile. Fetched on mount and
  // cached on MAV.credits (shared with usage.jsx / metrics.jsx) so the admin spend
  // reads the real account number even on a direct Admin-desk load — without this
  // the tile silently falls back to the DB sum when no other screen primed credits.
  const [credits, setCredits] = useAdminState(MAV.credits || null);

  // deep-link: a "Report an issue" elsewhere can hand off a ticket to open here
  React.useEffect(() => {
    if (window.__pendingIssue) { setTab('feedback'); setFbTab('issues'); setOpenIssueId(window.__pendingIssue); window.__pendingIssue = null; }
  }, []);

  React.useEffect(() => {
    let alive = true;
    if (window.GooseAPI && window.GooseAPI.openrouterCredits) {
      window.GooseAPI.openrouterCredits()
        .then((d) => { if (alive && d) { window.MAV.credits = d; setCredits(d); } })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, []);

  // an open ticket takes over the whole admin surface
  if (openIssueId) return <IssueTicket issueId={openIssueId} back={() => setOpenIssueId(null)} go={go} />;

  const onlineN = MAV.team.filter(m => m.status === 'online').length;
  // Total spend mirrors the Usage & budgets page (usage.jsx:45) EXACTLY so the two
  // admin spend views agree: real OpenRouter account usage when live, else our DB
  // build-cost sum (= the Dashboard's number), else the per-member rollup (0 until
  // builds carry owner_id). Tokens this month = sum of loaded builds' real tokensK
  // (no workspace token total in /summary yet). Rating / open-issues stay honest.
  const _sum = MAV.summary || {};
  const _cr = credits || MAV.credits || null;
  const _orLive = !!(_cr && _cr.available && _cr.remaining != null);
  const _dbCost = (_sum.total_cost != null) ? Number(_sum.total_cost) : 0;
  const _jobsTokK = (MAV.jobs || []).reduce((s, j) => s + (Number(j.tokensK) || 0), 0);
  const totalSpend = _orLive ? Number(_cr.total_usage) : (_dbCost || MAV.team.reduce((s, m) => s + (m.used || 0), 0));
  const totalTokK = _jobsTokK > 0 ? _jobsTokK : MAV.team.reduce((s, m) => s + (m.tokensK || 0), 0);
  const avgRating = (MAV.team.reduce((s, m) => s + m.rating, 0) / MAV.team.length);
  const openIssues = MAV.issues.filter(i => i.status === 'open').length;

  return (
    <div className="page fade-in">
      <div className="page-head">
        <div>
          <div className="row gap-10" style={{ marginBottom: 8 }}>
            <span className="chip" style={{ background: 'var(--brand-yellow)', color: 'var(--ink-on-brand)', fontWeight: 700 }}><I.shield size={13} /> Admin</span>
            <span className="chip">Mavlers workspace</span>
          </div>
          <h1 className="page-title">Admin desk</h1>
          <p className="page-desc">Who's flying, what they're spending, and how the squadron rates its wingman.</p>
        </div>
        <div className="row gap-10">
          <button className="btn btn-danger" onClick={() => exportAdminCSV(MAV)} title="Download all tracking data as CSV"><I.download size={15} /> Export CSV</button>
        </div>
      </div>

      {/* top stats */}
      <div className="stat-grid" style={{ marginBottom: 22 }}>
        <div className="stat">
          <div className="k"><I.users size={14} /> Active users</div>
          <div className="v">{MAV.team.length}<small> / 12 seats</small></div>
          <div className="sub up">{onlineN} online now</div>
        </div>
        <div className="stat">
          <div className="k"><I.coins size={14} /> Tokens this month</div>
          <div className="v">{(totalTokK / 1000).toFixed(1)}<small>M</small></div>
          <div className="sub">across {MAV.team.length} devs · {MAV.pods.length} teams</div>
        </div>
        <div className="stat">
          <div className="k"><I.bolt size={14} /> Total spend</div>
          <div className="v">${Number(totalSpend).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<small> / $3,000</small></div>
          <div className="sub">{Math.round(totalSpend / 3000 * 100)}% of workspace cap</div>
        </div>
        <div className="stat">
          <div className="k"><I.star size={14} /> Avg build rating</div>
          <div className="v">{avgRating.toFixed(1)}<small> / 5</small></div>
          <div className="sub">{openIssues} open {openIssues === 1 ? 'issue' : 'issues'}</div>
        </div>
      </div>

      {/* tab bar */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        <button className={'tab' + (tab === 'team' ? ' active' : '')} onClick={() => setTab('team')}>Team &amp; keys</button>
        {canManageMembers && <button className={'tab' + (tab === 'members' ? ' active' : '')} onClick={() => setTab('members')}>Members &amp; access</button>}
        <button className={'tab' + (tab === 'builds' ? ' active' : '')} onClick={() => setTab('builds')}>Builds by team</button>
        <button className={'tab' + (tab === 'feedback' ? ' active' : '')} onClick={() => setTab('feedback')}>Feedback &amp; issues</button>
        <button className={'tab' + (tab === 'kb' ? ' active' : '')} onClick={() => setTab('kb')}>KB approvals{MAV.kbRequests.filter(r => r.status === 'pending_review').length ? ' · ' + MAV.kbRequests.filter(r => r.status === 'pending_review').length : ''}</button>
        <button className={'tab' + (tab === 'cost' ? ' active' : '')} onClick={() => setTab('cost')}>Cost controls</button>
      </div>

      {tab === 'team' && <TeamKeys />}
      {tab === 'members' && <MembersAdmin />}
      {tab === 'builds' && <BuildsByTeam go={go} />}
      {tab === 'feedback' && <FeedbackIssues fbTab={fbTab} setFbTab={setFbTab} openIssues={openIssues} onOpenIssue={setOpenIssueId} />}
      {tab === 'kb' && <KBApprovals />}
      {tab === 'cost' && <CostControls />}
    </div>
  );
}

/* ============================================================
   Tab 1 · Team & keys
   ============================================================ */
const LIMIT_PRESETS = [150, 250, 500, 1000, 0]; // 0 = unlimited

function TeamKeys() {
  const team = window.MAV.team;
  // live, per-member key + limit state
  const [keys, setKeys] = useAdminState(() => Object.fromEntries(team.map(m => [m.id, { suffix: m.key, revoked: false }])));
  const [limits, setLimits] = useAdminState(() => Object.fromEntries(team.map(m => [m.id, m.limit])));
  const [openLim, setOpenLim] = useAdminState(null);

  const rotate = (id) => {
    const s = Math.random().toString(16).slice(2, 6);
    setKeys(k => ({ ...k, [id]: { suffix: s, revoked: false } }));
  };
  const revoke = (id) => setKeys(k => ({ ...k, [id]: { ...k[id], revoked: !k[id].revoked } }));
  const setLimit = (id, v) => { setLimits(l => ({ ...l, [id]: v })); setOpenLim(null); };
  const fmtLim = (v) => v === 0 ? '∞' : '$' + v;

  return (
    <div className="card card-pad fade-in">
      <div className="sec-head">
        <span className="sec-title">Members &amp; API keys</span>
        <span className="muted" style={{ fontSize: 12 }}>Per-dev OpenRouter keys · monthly spend caps</span>
      </div>

      <div className="team-head">
        <span>Member</span><span>Team</span><span>API key</span><span>Usage vs limit</span><span style={{ textAlign: 'right' }}>Actions</span>
      </div>

      {team.map(m => {
        const k = keys[m.id];
        const lim = limits[m.id];
        const pct = lim === 0 ? 12 : Math.min(100, Math.round(m.used / lim * 100));
        const over = lim !== 0 && pct >= 90;
        const pod = window.MAV.pods.find(p => p.id === m.pod);
        const path = window.MAV.teamPath(m.pod);
        return (
          <div key={m.id} className={'team-row' + (k.revoked ? ' revoked' : '')}>
            <MemberCell m={m} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: 'var(--ink-2)', fontWeight: 600 }}>{pod ? pod.name : '—'}</div>
              <div className="muted" style={{ fontSize: 10.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{path ? path.dept + ' › ' + path.subDept : ''}</div>
            </div>
            <div>
              <span className={'key-chip' + (k.revoked ? ' dead' : '')}>
                <I.key size={13} className="ki" /> sk-or-••••{k.suffix}
              </span>
              {k.revoked && <div className="pill pill-fail" style={{ marginTop: 6, fontSize: 10 }}><span className="dot"></span>Revoked</div>}
            </div>
            <div>
              <div className="usage-lbl">
                <span style={{ fontWeight: 600, color: over ? 'var(--cond)' : 'var(--ink-2)' }}>{pct}%</span>
                <span className="um">${Number(m.used).toFixed(2)} / {fmtLim(lim)}</span>
              </div>
              <div className={'meter' + (over ? ' warn' : '')} style={{ margin: 0, height: 7 }}><span style={{ width: pct + '%' }}></span></div>
            </div>
            <div className="row gap-8" style={{ justifyContent: 'flex-end', position: 'relative' }}>
              <div className="lim-wrap">
                <button className="icon-act" title="Set monthly limit" onClick={() => setOpenLim(openLim === m.id ? null : m.id)}><I.coins size={15} /></button>
                {openLim === m.id && (
                  <div className="lim-menu">
                    <div className="muted" style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '4px 11px 7px' }}>Monthly cap</div>
                    {LIMIT_PRESETS.map(v => (
                      <button key={v} className={lim === v ? 'sel' : ''} onClick={() => setLimit(m.id, v)}>
                        {v === 0 ? 'Unlimited' : '$' + v}{lim === v && <I.check size={14} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button className="icon-act" title={k.revoked ? 'Re-issue key' : 'Replace key'} onClick={() => k.revoked ? rotate(m.id) : rotate(m.id)}>
                <I.retry size={15} />
              </button>
              <button className={'icon-act danger'} title={k.revoked ? 'Key revoked' : 'Revoke key'} onClick={() => revoke(m.id)}>
                {k.revoked ? <I.check size={15} /> : <I.cancel size={15} />}
              </button>
            </div>
          </div>
        );
      })}

      <p className="muted" style={{ fontSize: 11.5, marginTop: 16, marginBottom: 0, lineHeight: 1.5 }}>
        <I.key size={12} style={{ verticalAlign: '-2px' }} /> Replacing a key rotates it instantly — the old secret stops working on the next build. Revoked devs can still sign in but can't launch a build until re-issued.
      </p>
    </div>
  );
}

/* ============================================================
   Tab 2 · Builds by team (hierarchy)
   ============================================================ */
function BuildsByTeam({ go }) {
  const { pods, team, org } = window.MAV;
  const [openPod, setOpenPod] = useAdminState(() => ((window.MAV.pods && window.MAV.pods[0]) || {}).id || null);
  const [openMember, setOpenMember] = useAdminState(null);

  const podMembers = (pid) => team.filter(m => m.pod === pid);
  const podAgg = (pid) => podMembers(pid).reduce((a, m) => ({ builds: a.builds + m.builds, tokK: a.tokK + m.tokensK, spend: a.spend + m.used }), { builds: 0, tokK: 0, spend: 0 });
  const maxPodBuilds = Math.max(...pods.map(p => podAgg(p.id).builds), 1);
  const totalBuilds = team.reduce((s, m) => s + m.builds, 0);
  const teamsIn = (deptId, subId) => pods.filter(p => p.dept === deptId && p.subDept === subId);
  const deptTeams = (deptId) => pods.filter(p => p.dept === deptId);
  const deptAgg = (deptId) => deptTeams(deptId).reduce((a, p) => { const g = podAgg(p.id); return { builds: a.builds + g.builds, spend: a.spend + g.spend }; }, { builds: 0, spend: 0 });

  const TeamRow = ({ p }) => {
    const agg = podAgg(p.id);
    const open = openPod === p.id;
    const mem = podMembers(p.id);
    const maxMem = Math.max(...mem.map(m => m.builds), 1);
    return (
      <div className="tree-pod">
        <button className="tree-row" onClick={() => setOpenPod(open ? null : p.id)}>
          <I.chevron size={16} className={'tree-chev' + (open ? ' open' : '')} />
          <div style={{ minWidth: 0 }}>
            <div className="nm" style={{ fontSize: 14 }}>{p.name}<span className="team-tag">team</span></div>
            <div className="muted" style={{ fontSize: 11.5 }}>Lead {p.lead} · {mem.length} devs · {p.clients}</div>
          </div>
          <div className="tree-bar" title={agg.builds + ' builds'}><span style={{ width: (agg.builds / maxPodBuilds * 100) + '%' }}></span></div>
          <span className="tree-num">{agg.builds} builds</span>
          <span className="tree-num" style={{ color: 'var(--muted)', textAlign: 'right' }}>${Number(agg.spend).toFixed(2)} · {(agg.tokK / 1000).toFixed(1)}M</span>
        </button>
        {open && (
          <div className="tree-members">
            {mem.map(m => {
              const mo = openMember === m.id;
              return (
                <div key={m.id}>
                  <button className="tree-mrow" onClick={() => setOpenMember(mo ? null : m.id)}>
                    <I.chevron size={14} className={'tree-chev' + (mo ? ' open' : '')} />
                    <div className="row gap-10" style={{ minWidth: 0 }}>
                      <Avatar m={m} size={26} />
                      <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                    </div>
                    <div className="tree-bar"><span style={{ width: (m.builds / maxMem * 100) + '%' }}></span></div>
                    <span className="tree-num">{m.builds} builds</span>
                    <span className="tree-num" style={{ color: 'var(--muted)', textAlign: 'right' }}>${Number(m.used).toFixed(2)} · {m.tokensK}K</span>
                  </button>
                  {mo && (
                    <div className="tmpl-rail">
                      {m.recent.map((t, i) => <span key={i} className="tmpl-chip"><I.doc size={12} className="muted" /> {t}</span>)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fade-in">
      <div className="row between wrap gap-12" style={{ marginBottom: 14 }}>
        <span className="muted" style={{ fontSize: 12.5 }}>
          <b style={{ color: 'var(--ink)' }}>{totalBuilds}</b> builds this month · department → sub-department → team → dev. Expand a team, then a dev, to see recent templates.
        </span>
        <button className="sec-link" onClick={() => go('builds')}>Open builds list <I.chevron size={14} /></button>
      </div>

      {org.departments.map(d => {
        const teamsHere = deptTeams(d.id);
        const dAgg = deptAgg(d.id);
        const empty = teamsHere.length === 0;
        return (
          <div key={d.id} className="org-dept">
            <div className="org-dept-head">
              <div className="row gap-10" style={{ alignItems: 'center', minWidth: 0 }}>
                <span className="org-dept-ico"><I.building size={15} /></span>
                <span className="org-dept-name">{d.name}</span>
                <span className="org-dept-kind">Department</span>
              </div>
              {empty
                ? <span className="chip" style={{ fontSize: 10.5 }}>Not onboarded yet</span>
                : <span className="muted mono" style={{ fontSize: 11.5 }}>{teamsHere.length} teams · {dAgg.builds} builds · ${Number(dAgg.spend).toFixed(2)}</span>}
            </div>
            {empty ? (
              <div className="org-empty">No teams in {d.name} yet — coming in a later rollout.</div>
            ) : (
              d.subDepts.filter(sd => teamsIn(d.id, sd.id).length).map(sd => (
                <div key={sd.id} className="org-sub">
                  <div className="org-sub-label"><span className="org-sub-line"></span>{sd.name}<span className="org-sub-kind">sub-dept</span></div>
                  <div className="org-sub-teams">
                    {teamsIn(d.id, sd.id).map(p => <TeamRow key={p.id} p={p} />)}
                  </div>
                </div>
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   Tab 3 · Feedback & issues
   ============================================================ */
function FeedbackIssues({ fbTab, setFbTab, openIssues, onOpenIssue }) {
  const { team, issues } = window.MAV;
  const avg = (team.reduce((s, m) => s + m.rating, 0) / team.length);
  const dist = [5, 4, 3, 2, 1].map(n => ({ n, c: team.filter(m => m.rating === n).length }));
  const maxC = Math.max(...dist.map(d => d.c));
  // feedback-signal aggregates
  const accPct = Math.round(avg / 5 * 100);
  const pass = window.MAV.passRate;
  const avgTime = (team.reduce((s, m) => s + (m.timeSaved || 0), 0) / team.length);
  const shipPct = Math.round(team.filter(m => m.wouldShip === 'ship').length / team.length * 100);
  const stages = window.MAV.stageScores;
  const maxStage = Math.max(...stages.map(s => s.helped + s.hurt));

  return (
    <div className="fade-in">
      <div className="row between wrap gap-12" style={{ marginBottom: 16 }}>
        <div className="tabs subtabs">
          <button className={'tab' + (fbTab === 'ratings' ? ' active' : '')} onClick={() => setFbTab('ratings')}>Ratings · {team.length}</button>
          <button className={'tab' + (fbTab === 'issues' ? ' active' : '')} onClick={() => setFbTab('issues')}>
            Issues · {issues.length}{openIssues > 0 && <span className="pill pill-cond" style={{ marginLeft: 7, fontSize: 9.5, padding: '1px 7px' }}>{openIssues} open</span>}
          </button>
        </div>
      </div>

      {fbTab === 'ratings' && (
        <div className="grid-2-1">
          <div className="col gap-10">
            {team.map(m => (
              <div key={m.id} className="rate-row">
                <Avatar m={m} />
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row between wrap gap-8">
                    <span className="nm" style={{ fontSize: 13.5 }}>{m.name}</span>
                    <Stars n={m.rating} />
                  </div>
                  <div className="rate-quote">"{m.comment}"</div>
                  <div className="rate-build"><I.doc size={11} /> {m.lastBuild}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="col gap-16" style={{ position: 'sticky', top: 84 }}>
            <div className="card card-pad">
              <div className="sec-head"><span className="sec-title">Wingman score</span></div>
              <div className="row gap-12" style={{ alignItems: 'baseline', marginBottom: 4 }}>
                <span className="page-title" style={{ fontSize: 40 }}>{avg.toFixed(1)}</span>
                <span className="muted">/ 5</span>
              </div>
              <Stars n={Math.round(avg)} size={18} />
              <p className="muted" style={{ fontSize: 12, margin: '12px 0 16px', lineHeight: 1.5 }}>How {team.length} devs rated their most recent build this week.</p>
              <div>
                {dist.map(d => (
                  <div key={d.n} className="dist-row">
                    <span className="dn">{d.n}<I.star size={11} fill="var(--brand-amber)" /></span>
                    <div className="meter" style={{ margin: 0, flex: 1, height: 8 }}><span style={{ width: (d.c / maxC * 100) + '%' }}></span></div>
                    <span className="um mono" style={{ fontSize: 11, color: 'var(--muted)', width: 18, textAlign: 'right' }}>{d.c}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* accuracy mapped to pass rate */}
            <div className="card card-pad">
              <div className="sec-head"><span className="sec-title">Accuracy vs pass rate</span></div>
              <div className="map-row"><span className="map-k">Dev accuracy</span><div className="meter" style={{ margin: 0, flex: 1, height: 8 }}><span style={{ width: accPct + '%' }}></span></div><span className="map-v">{accPct}%</span></div>
              <div className="map-row"><span className="map-k">QA pass rate</span><div className="meter" style={{ margin: 0, flex: 1, height: 8 }}><span style={{ width: pass + '%', background: 'var(--pass)' }}></span></div><span className="map-v">{pass}%</span></div>
              <p className="muted" style={{ fontSize: 11.5, margin: '10px 0 0', lineHeight: 1.5 }}>
                {accPct >= pass
                  ? 'Devs rate builds at or above the QA pass rate — the pipeline is earning trust.'
                  : 'Devs rate ' + (pass - accPct) + ' pts below QA pass — a sign QA is passing builds the team would still touch.'}
              </p>
            </div>

            {/* signal tiles */}
            <div className="row gap-12">
              <div className="card card-pad grow" style={{ padding: 16 }}>
                <div className="muted" style={{ fontSize: 11 }}><I.clock size={12} style={{ verticalAlign: '-2px' }} /> Avg time saved</div>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginTop: 6 }}>{avgTime.toFixed(1)}<small style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 600 }}> hrs</small></div>
                <div className="muted mono" style={{ fontSize: 10.5, marginTop: 4 }}>per build · self-reported</div>
              </div>
              <div className="card card-pad grow" style={{ padding: 16 }}>
                <div className="muted" style={{ fontSize: 11 }}><I.check size={12} style={{ verticalAlign: '-2px' }} /> Ship as-is</div>
                <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', marginTop: 6 }}>{shipPct}<small style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 600 }}>%</small></div>
                <div className="muted mono" style={{ fontSize: 10.5, marginTop: 4 }}>no edits needed</div>
              </div>
            </div>

            {/* stage signal */}
            <div className="card card-pad">
              <div className="sec-head"><span className="sec-title">Stage signal</span><span className="muted" style={{ fontSize: 11 }}>helped vs cleanup</span></div>
              {stages.map(s => {
                const total = s.helped + s.hurt;
                const hp = Math.round(s.helped / total * 100);
                return (
                  <div key={s.id} className="stagesig-row">
                    <span className="stagesig-nm">{s.name}</span>
                    <div className="stagesig-bar" title={s.helped + ' helped · ' + s.hurt + ' cleanup'}>
                      <span className="sig-up" style={{ width: hp + '%' }}></span>
                      <span className="sig-down" style={{ width: (100 - hp) + '%' }}></span>
                    </div>
                    <span className="stagesig-v">{hp}%</span>
                  </div>
                );
              })}
              <p className="muted" style={{ fontSize: 11, margin: '10px 0 0', lineHeight: 1.5 }}>Execution draws the most cleanup flags — the weak link the team keeps citing.</p>
            </div>
          </div>
        </div>
      )}

      {fbTab === 'issues' && <IssuesDirectory onOpenIssue={onOpenIssue} />}
    </div>
  );
}

Object.assign(window, { AdminDesk });
